import { useEffect, useState, useMemo, useRef } from 'react'
import { Control, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EducationFunctions } from './EducationFunctions'
import { IcfesControl } from './IcfesControl'

import { ContainerGroupField } from '@/components/common/ContainerGroupField'
import { LittleText, Text } from '@/components/common/UI'
import {
    AutocompleteControl,
    SwitchControl,
    SelectControl,
    InputControl
} from '@/components/form-controls'
import { Chip } from '@/components/nextui'
import {
    EducationFormData,
    InstitutionOption
} from '@/types/forms/educationForm'
import { OptionsField } from '@/types/forms/optionsField'

// Special ID for "Other" option
const OTHER_OPTION_ID = 'OTHER_INSTITUTION'

const SCHOOL_WINDOW_SIZE = 40

interface SchoolControlProps {
    control: Control<EducationFormData>
    text: string
    optionsTittles: OptionsField[]
    watchIcfes: boolean | undefined
    watchSchool: string | null | undefined
    initialName?: string
    setValue: UseFormSetValue<EducationFormData>
    studyStatusOptions: OptionsField[]
    // Location props
    items: {
        country?: string
        department?: string
        city?: string
    }
}

// Component for rendering school location info
const SchoolLocationInfo = ({
    selectedSchool,
    newText,
    text
}: {
    selectedSchool: InstitutionOption
    newText: string
    text: string
}): JSX.Element | null => {
    const { t } = useTranslation()

    return (
        <article className="p-2 space-y-2">
            <div className="flex items-center space-x-2">
                <Text text={t(`${newText}.schoolCountry`)} />
                <LittleText
                    text={selectedSchool?.country || t(`${text}.noAvailable`)}
                />
            </div>
            <div className="flex items-center space-x-2">
                <Text text={t(`${newText}.schoolDepartment`)} />
                <LittleText
                    text={
                        selectedSchool?.department || t(`${text}.noAvailable`)
                    }
                />
            </div>
            <div className="flex items-center space-x-2">
                <Text text={t(`${newText}.schoolCity`)} />
                <LittleText
                    text={selectedSchool?.city || t(`${text}.noAvailable`)}
                />
            </div>
        </article>
    )
}

// Component for rendering school option chips
const SchoolOptionChips = ({
    option
}: {
    option: InstitutionOption
}): JSX.Element | null => {
    const { t } = useTranslation()

    if (!option.city && !option.country && !option.department) {
        return null
    }

    return (
        <div className="flex gap-1 flex-wrap">
            {option.country && (
                <Chip
                    variant="bordered"
                    size="sm"
                >
                    {option.country}
                </Chip>
            )}
            {option.department && (
                <Chip
                    color="primary"
                    size="sm"
                >
                    {option.department}
                </Chip>
            )}
            {option.city && (
                <Chip
                    color="secondary"
                    size="sm"
                >
                    {option.city}
                </Chip>
            )}
        </div>
    )
}

// Custom hook to handle school selection logic
const useSchoolSelection = (
    initialName: string | undefined,
    watchSchool: string | null | undefined,
    items: { country?: string; department?: string; city?: string },
    getInstitutions: (search: string, isSchool: boolean) => Promise<any>,
    t: (key: string) => string
) => {
    const [searchSchoolName, setSearchSchoolName] = useState('')

    // Create "Other" option first (needed for initial state)
    const otherOption: InstitutionOption = useMemo(
        () => ({
            value: OTHER_OPTION_ID,
            label: t('educationInformation.otherInstitution'),
            code: OTHER_OPTION_ID,
            country: '',
            department: '',
            city: ''
        }),
        [t]
    )

    const initialOption: InstitutionOption | null = useMemo(() => {
        if (initialName && watchSchool) {
            // Si el watchSchool ya es "OTHER_INSTITUTION", devolver la opción "Otro"
            if (watchSchool === OTHER_OPTION_ID) {
                return otherOption
            }

            return {
                label: initialName,
                value: watchSchool,
                code: watchSchool,
                country: items.country || '',
                department: items.department || '',
                city: items.city || ''
            }
        }

        return null
    }, [initialName, watchSchool, items, otherOption])

    const [optionsSchools, setOptionsSchools] = useState<InstitutionOption[]>(
        initialOption ? [initialOption] : []
    )
    const [selectedSchool, setSelectedSchool] =
        useState<InstitutionOption | null>(initialOption)

    // Update selectedSchool when initialOption changes (preloaded data)
    useEffect(() => {
        if (initialOption) {
            setSelectedSchool(initialOption)
        }
    }, [initialOption])

    // Sync selectedSchool when watchSchool changes
    useEffect(() => {
        if (watchSchool && !selectedSchool) {
            const found = optionsSchools.find(
                (opt) => String(opt.value) === String(watchSchool)
            )

            if (found) {
                setSelectedSchool(found)
            }
        }
    }, [watchSchool, selectedSchool, optionsSchools])

    useEffect(() => {
        const fetchSchools = async (): Promise<void> => {
            const schools = await getInstitutions(searchSchoolName, true)

            if (schools && schools.length > 0) {
                // Always add "Other" option at the end
                setOptionsSchools([...schools, otherOption])
            } else {
                // Even when no results, show "Other" option
                setOptionsSchools([otherOption])
            }
        }

        if (searchSchoolName.length >= 6) {
            fetchSchools()
        } else {
            // Before 6 characters, show initial option
            setOptionsSchools((prev) => {
                if (initialOption) {
                    const current = prev.find(
                        (opt) => opt.value === initialOption.value
                    )

                    if (current) {
                        return [current]
                    }

                    return [initialOption]
                }

                return []
            })
        }
    }, [searchSchoolName, initialOption, otherOption])

    return {
        searchSchoolName,
        setSearchSchoolName,
        optionsSchools,
        selectedSchool,
        setSelectedSchool
    }
}

// Sub-component for School Autocomplete to reduce main component size
interface SchoolAutocompleteProps {
    control: Control<EducationFormData>
    options: InstitutionOption[]
    searchName: string
    setSearch: (value: string) => void
    t: (key: string) => string
    text: string
    newText: string
    onSelectionChange: (selected: InstitutionOption | null) => void
    setValue: UseFormSetValue<EducationFormData>
}

const SchoolAutocomplete = ({
    control,
    options,
    searchName,
    setSearch,
    t,
    text,
    newText,
    onSelectionChange,
    setValue
}: SchoolAutocompleteProps) => {
    const [windowStart, setWindowStart] = useState(0)
    const listRef = useRef<HTMLDivElement | null>(null)
    const pagingLockRef = useRef(false)

    // Reset window when search changes or options reload
    useEffect(() => {
        setWindowStart(0)
        if (listRef.current) {
            listRef.current.scrollTop = 0
        }
    }, [searchName, options.length])

    const displayedOptions = useMemo(() => {
        if (options.length <= SCHOOL_WINDOW_SIZE) {
            return options
        }

        const end = Math.min(windowStart + SCHOOL_WINDOW_SIZE, options.length)

        return options.slice(windowStart, end)
    }, [options, windowStart])

    const handleScroll = (e: any) => {
        const target = e.target as HTMLDivElement
        const nearBottom =
            target.scrollTop + target.clientHeight >= target.scrollHeight - 10
        const nearTop = target.scrollTop <= 0

        if (pagingLockRef.current) {
            return
        }

        const step = Math.floor(SCHOOL_WINDOW_SIZE / 2)

        if (nearBottom) {
            if (windowStart + SCHOOL_WINDOW_SIZE < options.length) {
                pagingLockRef.current = true

                // Avanzamos solo la mitad de la ventana
                setWindowStart((prev) =>
                    Math.min(
                        prev + step,
                        Math.max(options.length - SCHOOL_WINDOW_SIZE, 0)
                    )
                )

                // Ajustamos el scroll hacia la mitad para evitar saltos bruscos al inicio
                // y dar la sensación de continuidad
                setTimeout(() => {
                    if (listRef.current) {
                        // Posicionamos el scroll aprox en la mitad
                        listRef.current.scrollTop =
                            listRef.current.scrollHeight / 2
                    }
                    pagingLockRef.current = false
                }, 50)
            }
        } else if (nearTop) {
            if (windowStart > 0) {
                pagingLockRef.current = true

                setWindowStart((prev) => Math.max(prev - step, 0))

                setTimeout(() => {
                    if (listRef.current) {
                        listRef.current.scrollTop =
                            listRef.current.scrollHeight / 2
                    }
                    pagingLockRef.current = false
                }, 50)
            }
        }
    }

    return (
        <div className="flex flex-col gap-2">
            <AutocompleteControl
                name="schoolId"
                control={control}
                label={t(`${newText}.schoolName`)}
                options={displayedOptions}
                rules={{
                    required: t(`${text}.required.institutionName`)
                }}
                disableLocalFilter={true}
                listboxProps={{
                    ref: listRef,
                    onScroll: handleScroll,
                    className: 'max-h-[300px] overflow-y-auto',
                    emptyContent: (
                        <div className="px-3 py-2 text-sm text-gray-500 italic">
                            {searchName.length < 6
                                ? 'Escribe al menos 6 letras para buscar'
                                : 'No se encuentran colegios'}
                        </div>
                    ),
                    topContent:
                        searchName.length >= 6 &&
                        options.length === 1 &&
                        options[0].value === 'OTHER_INSTITUTION' ? (
                            <div className="px-3 py-2 text-sm text-gray-500 bg-gray-50 border-b">
                                <p className="font-medium text-gray-700">
                                    No se encuentran colegios
                                </p>
                                <p className="text-xs mt-1">
                                    Puedes seleccionar la opción
                                    &quot;Otro&quot; para escribir manualmente
                                </p>
                            </div>
                        ) : null,
                    bottomContent:
                        options.length > 1 && options.length === 51 ? (
                            <div className="px-3 py-2 text-xs text-blue-600 bg-blue-50 border-t">
                                Mostrando primeros 50 resultados. Sé más
                                específico para refinar la búsqueda.
                            </div>
                        ) : null
                }}
                onOpenChange={(open: boolean) => {
                    if (open) {
                        setWindowStart(0)
                        if (listRef.current) {
                            listRef.current.scrollTop = 0
                        }
                    }
                }}
                popoverProps={{
                    offset: 4,
                    placement: 'bottom'
                }}
                customRender={(option) => (
                    <div className="flex flex-col gap-1 py-1">
                        <span className="text-sm font-medium text-gray-900 truncate">
                            {t(option.label)}
                        </span>
                        <SchoolOptionChips option={option} />
                    </div>
                )}
                onInputChange={setSearch}
                onChangeCustom={(value) => {
                    const selected = options.find(
                        (opt) => String(opt.value) === String(value)
                    )

                    if (selected) {
                        // Si selecciona "Otro", limpiar datos de ubicación
                        if (selected.value === OTHER_OPTION_ID) {
                            setValue('schoolName', '')
                            setValue('countrySchool', '')
                            setValue('departmentSchool', '')
                            setValue('municipalitySchool', '')
                        } else {
                            // Guardar el nombre y ubicación en el formulario
                            setValue('schoolName', selected.label)
                            setValue('countrySchool', selected.country)
                            setValue('departmentSchool', selected.department)
                            setValue('municipalitySchool', selected.city)
                        }
                    }

                    onSelectionChange(selected || null)
                }}
            />
        </div>
    )
}

export const SchoolControl = ({
    control,
    optionsTittles,
    watchIcfes,
    watchSchool,
    initialName,
    text,
    setValue,
    studyStatusOptions,
    items
}: SchoolControlProps): JSX.Element => {
    const { t } = useTranslation()
    const { getInstitutions } = EducationFunctions()
    const hasInitialized = useRef(false)

    const {
        searchSchoolName,
        setSearchSchoolName,
        optionsSchools,
        selectedSchool,
        setSelectedSchool
    } = useSchoolSelection(initialName, watchSchool, items, getInstitutions, t)

    const newText = `${text}.school`
    const isOtherSelected = watchSchool === OTHER_OPTION_ID

    // Effect to handle legacy data (nombre sin código) - auto-seleccionar "Otro"
    useEffect(() => {
        // Solo para datos legacy: si hay nombre pero no hay código, seleccionar "Otro"
        if (
            initialName &&
            (!watchSchool || watchSchool === '') &&
            !hasInitialized.current
        ) {
            setValue('schoolId', OTHER_OPTION_ID, { shouldValidate: false })
            setValue('customSchoolName', initialName, { shouldValidate: false })
            hasInitialized.current = true
        } else if (initialName && watchSchool) {
            hasInitialized.current = true
        }
    }, [initialName, watchSchool, setValue])

    // Clear custom name when switching away from "Other" - pero solo después de inicializar
    useEffect(() => {
        // Solo limpiar si ya se inicializó y el usuario cambió de opción
        if (
            hasInitialized.current &&
            !isOtherSelected &&
            watchSchool &&
            watchSchool !== OTHER_OPTION_ID
        ) {
            setValue('customSchoolName', undefined)
        }
    }, [isOtherSelected, watchSchool, setValue])

    return (
        <ContainerGroupField tittleGroup={t('forms.groups.school')}>
            <SchoolAutocomplete
                control={control}
                options={optionsSchools}
                searchName={searchSchoolName}
                setSearch={setSearchSchoolName}
                t={t}
                text={text}
                newText={newText}
                onSelectionChange={setSelectedSchool}
                setValue={setValue}
            />

            {/* Custom input field when "Other" is selected */}
            {isOtherSelected && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                    <InputControl
                        name="customSchoolName"
                        control={control}
                        label={t('educationInformation.customSchoolName')}
                        placeholder={t(
                            'educationInformation.customSchoolPlaceholder'
                        )}
                        rules={{
                            required: t(`${text}.required.institutionName`)
                        }}
                        description={t(
                            'educationInformation.customInstitutionDescription'
                        )}
                        maxWords={200}
                        capitalize={true}
                        onChangeCustom={(value) => {
                            // Store custom name as school name
                            setValue('schoolName', value)
                        }}
                    />
                </div>
            )}

            {watchSchool && selectedSchool && !isOtherSelected && (
                <SchoolLocationInfo
                    selectedSchool={selectedSchool}
                    newText={newText}
                    text={text}
                />
            )}
            <AutocompleteControl
                name="titleSchool"
                control={control}
                label={t(`${text}.tittle`)}
                options={optionsTittles}
                rules={{
                    required: t(`${text}.required.tittle`)
                }}
            />

            <SelectControl
                name="schoolStudyStatus"
                control={control}
                label={t('educationInformation.homologate.statusEducation')}
                options={studyStatusOptions}
                rules={{
                    required: t(
                        'educationInformation.homologate.required.statusEducation'
                    )
                }}
            />

            <SwitchControl
                name="isIcfesReceived"
                control={control}
                label={t(`${newText}.icfes`)}
            />

            {watchIcfes && (
                <div className="col-span-2">
                    <IcfesControl
                        control={control}
                        setValue={setValue}
                        text={newText}
                        watchIcfes={watchIcfes}
                    />
                </div>
            )}
        </ContainerGroupField>
    )
}
